import React, { useState } from 'react';
import { Form, Input, Button, Typography, useToast } from '@crisellevaldez/speculo-ui';
import { Copy, Save, CheckCircle } from 'lucide-react';
import { Organization } from '../../../../types/organization.types';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// Define schema for Follow Up Boss API key
const apiKeySchema = z.object({
  api_key: z.string().min(1, 'API Key is required'),
});

type ApiKeyFormData = z.infer<typeof apiKeySchema>;

interface FubApiKeySetupProps {
  selectedOrg?: Organization;
  isConfigured: boolean;
  onApiKeySaved: () => void;
  storeApiKey: (orgId: string, apiKey: string, isFirstTimeSetup: boolean) => Promise<boolean>;
  isStoringApiKey: boolean;
}

export const FubApiKeySetup: React.FC<FubApiKeySetupProps> = ({
  selectedOrg,
  isConfigured,
  onApiKeySaved,
  storeApiKey,
  isStoringApiKey,
}) => {
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const { showToast } = useToast();

  const form = useForm<ApiKeyFormData>({
    resolver: zodResolver(apiKeySchema),
    defaultValues: {
      api_key: '',
    },
  });

  // Function to save the API key
  const handleSaveKey = async () => {
    if (!selectedOrg?.id) return;

    try {
      // Validate the form
      const isValid = await form.trigger();
      if (!isValid) return;

      const apiKey = form.getValues('api_key');

      // Determine if this is the first time setup
      const isFirstTimeSetup = !isConfigured;

      const success = await storeApiKey(selectedOrg.id, apiKey, isFirstTimeSetup);

      if (success) {
        setApiKeyVisible(true);

        // Show appropriate message based on whether this was first time setup
        if (isFirstTimeSetup) {
          showToast({
            message: 'API key saved successfully. You can now proceed to step 2.',
            variant: 'success',
          });
        } else {
          showToast({
            message: 'API key updated successfully',
            variant: 'success',
          });
        }

        onApiKeySaved();
      } else {
        showToast({
          message: 'Failed to save API key',
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error saving API key:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to save API key',
        variant: 'error',
      });
    }
  };

  // Function to copy API key to clipboard
  const handleCopyKey = () => {
    const apiKey = form.getValues('api_key');
    if (apiKey) {
      navigator.clipboard
        .writeText(apiKey)
        .then(() => {
          showToast({
            message: 'API key copied to clipboard',
            variant: 'success',
          });
        })
        .catch((error) => {
          console.error('Error copying API key:', error);
          showToast({
            message: 'Failed to copy API key',
            variant: 'error',
          });
        });
    }
  };

  return (
    <div>
      <form className="space-y-4">
        <Form.Control required>
          <Form.Label>API Key</Form.Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                {...form.register('api_key')}
                type={apiKeyVisible ? 'text' : 'password'}
                placeholder="Enter your Follow Up Boss API key"
                error={form.formState.errors.api_key?.message}
                disabled={isStoringApiKey}
              />
            </div>
            {apiKeyVisible && (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={handleCopyKey}
                className="h-8"
                disabled={isStoringApiKey}
              >
                <Copy className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="primary"
              type="button"
              onClick={handleSaveKey}
              disabled={isStoringApiKey}
              isLoading={isStoringApiKey}
              className="flex items-center gap-1 h-8"
            >
              <Save className="h-4 w-4" />
              Save
            </Button>
          </div>
          <Form.HelperText>
            {isConfigured
              ? 'API key is configured. Enter a new key and click Save to update.'
              : 'Enter your Follow Up Boss API key and click Save to configure the integration.'}
          </Form.HelperText>
        </Form.Control>
      </form>
    </div>
  );
};
